import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, KeyRound, ShieldAlert, XCircle } from 'lucide-react';
import toast from '../../design-system/toast';

import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useOrganization } from '../../contexts/OrganizationContext';
import { apiEndpoints } from '../../utils/api';

type FetchedStatus = 'pending' | 'approved' | 'consumed' | 'denied';

interface DeviceAuthorizationView {
  user_code: string;
  client_name?: string | null;
  status: FetchedStatus;
  requested_at: string;
  expires_at: string;
}

type ViewState =
  | { kind: 'paste_code' }
  | { kind: 'loading' }
  | { kind: 'pending'; data: DeviceAuthorizationView }
  | { kind: 'approved' }
  | { kind: 'denied' }
  | { kind: 'error_not_found' }
  | { kind: 'error_already_used' }
  | { kind: 'error_generic'; message: string };

const DEFAULT_DEVICE_LABEL = 'CLI device';

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-ui-background-primary flex items-center justify-center p-4">
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl shadow-sm w-full max-w-md p-6 space-y-4">
      {children}
    </div>
  </div>
);

const DeviceApprovalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUserCode = searchParams.get('user_code') ?? '';
  const api = useAuthenticatedApi();
  const { organizations } = useOrganization();

  const [view, setView] = useState<ViewState>(
    initialUserCode ? { kind: 'loading' } : { kind: 'paste_code' }
  );
  const [pastedCode, setPastedCode] = useState('');
  const [deviceLabel, setDeviceLabel] = useState(DEFAULT_DEVICE_LABEL);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const adminOrgs = useMemo(
    () =>
      organizations.filter(
        (org) => org.current_user_role === 'admin' || org.current_user_role === 'owner'
      ),
    [organizations]
  );

  const loadDeviceAuthorization = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;

      setView({ kind: 'loading' });
      try {
        const data = await api.get<DeviceAuthorizationView>(apiEndpoints.deviceAuth.get(trimmed));
        if (data.status !== 'pending') {
          setView({ kind: 'error_already_used' });
          return;
        }
        setView({ kind: 'pending', data });
      } catch (err: any) {
        if (err?.status === 404) {
          setView({ kind: 'error_not_found' });
          return;
        }
        const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to load device';
        setView({ kind: 'error_generic', message: typeof message === 'string' ? message : 'Failed to load device' });
      }
    },
    [api]
  );

  useEffect(() => {
    if (initialUserCode) {
      void loadDeviceAuthorization(initialUserCode);
    }
    // We deliberately load only when initialUserCode changes — the user can
    // also trigger via the paste form which calls the loader directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserCode]);

  // Default the org dropdown to the first admin org once orgs load.
  useEffect(() => {
    if (!selectedOrgId && adminOrgs.length > 0) {
      setSelectedOrgId(adminOrgs[0].id);
    }
  }, [adminOrgs, selectedOrgId]);

  const handlePasteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pastedCode.trim()) return;
    setSearchParams({ user_code: pastedCode.trim() });
    void loadDeviceAuthorization(pastedCode.trim());
  };

  const handleApprove = async () => {
    if (view.kind !== 'pending') return;
    if (!selectedOrgId) {
      toast.error('Pick an organization to approve for');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(apiEndpoints.deviceAuth.approve(view.data.user_code), {
        organization_id: selectedOrgId,
        device_label: deviceLabel.trim() || DEFAULT_DEVICE_LABEL,
      });
      setView({ kind: 'approved' });
      toast.success('Device approved');
    } catch (err: any) {
      const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to approve';
      toast.error(typeof message === 'string' ? message : 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (view.kind !== 'pending') return;
    setSubmitting(true);
    try {
      await api.post(apiEndpoints.deviceAuth.deny(view.data.user_code));
      setView({ kind: 'denied' });
      toast.success('Device denied');
    } catch (err: any) {
      const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to deny';
      toast.error(typeof message === 'string' ? message : 'Failed to deny');
    } finally {
      setSubmitting(false);
    }
  };

  if (view.kind === 'loading') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-6">
          <Spinner size={32} />
          <p className="text-sm text-ui-text-tertiary">Loading device authorization…</p>
        </div>
      </Card>
    );
  }

  if (view.kind === 'paste_code') {
    return (
      <Card>
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode" />
          <h1 className="text-lg font-semibold text-ui-text-primary">Connect a device</h1>
        </div>
        <p className="text-sm text-ui-text-secondary">
          Enter the code shown in your terminal to continue.
        </p>
        <form onSubmit={handlePasteSubmit} className="space-y-3">
          <input
            type="text"
            value={pastedCode}
            onChange={(e) => setPastedCode(e.target.value)}
            placeholder="XXXX-XXXX"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="w-full px-4 py-2 font-mono uppercase tracking-widest border border-ui-border-primary rounded-lg
                       bg-ui-background-secondary text-ui-text-primary
                       placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                       focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
          <button
            type="submit"
            disabled={!pastedCode.trim()}
            className="w-full px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark
                       disabled:opacity-50 rounded-lg transition-colors"
          >
            Continue
          </button>
        </form>
      </Card>
    );
  }

  if (view.kind === 'error_not_found') {
    return (
      <Card>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <h1 className="text-lg font-semibold text-ui-text-primary">Code not found</h1>
        </div>
        <p className="text-sm text-ui-text-secondary">
          This code is invalid or has expired. Run the CLI again to get a new code.
        </p>
      </Card>
    );
  }

  if (view.kind === 'error_already_used') {
    return (
      <Card>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          <h1 className="text-lg font-semibold text-ui-text-primary">Code already used</h1>
        </div>
        <p className="text-sm text-ui-text-secondary">
          This device authorization is no longer pending. Run the CLI again if you need a new pairing.
        </p>
      </Card>
    );
  }

  if (view.kind === 'error_generic') {
    return (
      <Card>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <h1 className="text-lg font-semibold text-ui-text-primary">Something went wrong</h1>
        </div>
        <p className="text-sm text-ui-text-secondary">{view.message}</p>
      </Card>
    );
  }

  if (view.kind === 'approved') {
    return (
      <Card>
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <h1 className="text-lg font-semibold text-ui-text-primary">Device connected</h1>
        </div>
        <p className="text-sm text-ui-text-secondary">
          You can return to your terminal — your CLI will continue automatically. The new key will appear in
          Organization Settings → API Keys, where you can revoke it any time.
        </p>
      </Card>
    );
  }

  if (view.kind === 'denied') {
    return (
      <Card>
        <div className="flex items-center gap-2">
          <XCircle className="w-5 h-5 text-red-500" />
          <h1 className="text-lg font-semibold text-ui-text-primary">Device denied</h1>
        </div>
        <p className="text-sm text-ui-text-secondary">You can close this tab.</p>
      </Card>
    );
  }

  // view.kind === 'pending'
  const data = view.data;
  const noAdminOrgs = adminOrgs.length === 0;

  return (
    <Card>
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode" />
        <h1 className="text-lg font-semibold text-ui-text-primary">Connect a device</h1>
      </div>

      <p className="text-sm text-ui-text-secondary">
        A device wants to connect to your Kolega.dev organization. The code shown in your terminal is{' '}
        <span className="font-mono font-medium text-ui-text-primary">{data.user_code}</span>. If
        it doesn't match, do <strong>not</strong> approve.
      </p>

      {data.client_name && (
        <p className="text-xs text-ui-text-tertiary">
          Reported client: <span className="font-mono">{data.client_name}</span>
        </p>
      )}

      {noAdminOrgs ? (
        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            You must be an admin or owner of an organization to approve a device. Ask an admin to run the
            approval, or sign in as one.
          </p>
        </div>
      ) : (
        <>
          <div>
            <label
              htmlFor="org-select"
              className="block text-sm font-medium text-ui-text-secondary mb-1"
            >
              Organization
            </label>
            <select
              id="org-select"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              disabled={submitting}
              className="w-full px-4 py-2 border border-ui-border-primary rounded-lg
                         bg-ui-background-secondary text-ui-text-primary
                         focus:ring-2 focus:ring-brand-primary focus:border-transparent
                         disabled:opacity-50"
            >
              {adminOrgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="device-label"
              className="block text-sm font-medium text-ui-text-secondary mb-1"
            >
              Device label
            </label>
            <input
              id="device-label"
              type="text"
              value={deviceLabel}
              onChange={(e) => setDeviceLabel(e.target.value)}
              maxLength={80}
              disabled={submitting}
              className="w-full px-4 py-2 border border-ui-border-primary rounded-lg
                         bg-ui-background-secondary text-ui-text-primary
                         focus:ring-2 focus:ring-brand-primary focus:border-transparent
                         disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-ui-text-tertiary">
              Becomes the API key name in Organization Settings → API Keys.
            </p>
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={handleDeny}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700
                     disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2"
        >
          {submitting ? <Spinner size={16} tone="current" /> : <XCircle className="w-4 h-4" />}
          Deny
        </button>
        <button
          type="button"
          onClick={handleApprove}
          disabled={submitting || noAdminOrgs || !selectedOrgId}
          className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark
                     disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
        >
          {submitting ? <Spinner size={16} tone="current" /> : <CheckCircle className="w-4 h-4" />}
          Approve
        </button>
      </div>
    </Card>
  );
};

export default DeviceApprovalPage;
